import { useCallback, useRef, useState } from "react";
import { lookupWord } from "@/api/words";
import type { WordLookupResult } from "@/types/word";

export interface WordLookupController {
  word: string;
  sentence: string;
  result: WordLookupResult | null;
  loading: boolean;
  anchorRect: DOMRect | null;
  /** Look a word up (or serve it from cache) and anchor the popover to `rect`. */
  open: (word: string, sentence: string, rect: DOMRect) => void;
  close: () => void;
  /** Patch the cache so a lemma's tracked state survives re-opening the popover. */
  markTracked: (lemma: string, surface: string, tracked?: boolean) => void;
}

/**
 * Word-lookup popover state for the reading view: the clicked word, its
 * lookup result, and an in-session cache of single-interpretation results.
 * Pass the reading's `languageId`; lookups no-op until it's known.
 */
export function useWordLookup(languageId: number | undefined): WordLookupController {
  const [word, setWord] = useState("");
  const [sentence, setSentence] = useState("");
  const [result, setResult] = useState<WordLookupResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
  const cache = useRef(new Map<string, WordLookupResult>());

  const open = useCallback(
    (clicked: string, ctx: string, rect: DOMRect) => {
      if (languageId === undefined) return;

      setWord(clicked);
      setSentence(ctx);
      setAnchorRect(rect);

      const key = clicked.toLowerCase();
      const cached = cache.current.get(key);
      if (cached) {
        setResult(cached);
        setLoading(false);
        return;
      }

      setResult(null);
      setLoading(true);
      lookupWord(languageId, clicked, ctx)
        .then((res) => {
          const data = res.data.data;
          // Only single-interpretation results are stable enough to cache.
          if (data.found && data.words && data.words.length <= 1) {
            cache.current.set(key, data);
          }
          setResult(data);
        })
        .catch(() => setResult({ found: false }))
        .finally(() => setLoading(false));
    },
    [languageId],
  );

  const close = useCallback(() => {
    setWord("");
    setAnchorRect(null);
    setResult(null);
  }, []);

  const markTracked = useCallback((lemma: string, surface: string, tracked = true) => {
    cache.current.forEach((cached, key) => {
      if (key === surface.toLowerCase() && cached.words) {
        const words = cached.words.map((w) =>
          w.lemma.toLowerCase() === lemma.toLowerCase()
            ? { ...w, tracked, known: tracked ? w.known : false }
            : w,
        );
        cache.current.set(key, { ...cached, words });
      }
    });
  }, []);

  return { word, sentence, result, loading, anchorRect, open, close, markTracked };
}
